// ============================================================================
// Client portal API — the SELF-SERVICE realm (/api/client/*).
// ----------------------------------------------------------------------------
// Entirely separate from the trainer surface: a logged-in CLIENT reads/writes
// ONLY their own data through these endpoints. There is no client_id in any URL
// — the server resolves "me" from the vf_client_session cookie. The cookie
// rides along automatically (credentials: "include", set in http.ts).
// ============================================================================
import { API_BASE, http } from "./http";
import type {
  Activity,
  CalendarSubscription,
  ClientActivityInput,
  ClientAuthResponse,
  ClientCalendarEntry,
  ForgotResponse,
  ClientClaimPayload,
  ClientDashboard,
  ClientForgotPayload,
  ClientHome,
  ClientLoginPayload,
  ClientPasswordChangePayload,
  ClientReport,
  ClientWeeklyGoalResponse,
  Goal,
  Meal,
  MealInput,
  MealListResponse,
  MealPhotoResponse,
  Measurement,
  MeasurementInput,
  Message,
  MessageAttachmentResponse,
  MessageInput,
  MessageListResponse,
  MessageUnreadResponse,
  MetricsSummary,
  NutritionTargets,
  OkResponse,
  PasswordResetPayload,
  PortalClient,
  Session,
  SessionInput,
  WaterInput,
  WaterLog,
} from "./types";

/** Inclusive [start,end] ISO bounds for the client calendar-entries union. */
export interface ClientCalendarRange {
  start?: string;
  end?: string;
  // Index signature keeps this assignable to the http `Query` param type.
  [key: string]: string | undefined;
}

export const clientApi = {
  // --- auth ---------------------------------------------------------------
  claim: (body: ClientClaimPayload) =>
    http.post<ClientAuthResponse>("/client/claim", body),
  login: (body: ClientLoginPayload) =>
    http.post<ClientAuthResponse>("/client/login", body),
  logout: () => http.post<OkResponse>("/client/logout"),
  me: (signal?: AbortSignal) =>
    http.get<PortalClient>("/client/me", undefined, signal),
  setLocale: (locale: string) =>
    http.patch<PortalClient>("/client/me", { locale }),
  /**
   * Wave 8 — request a CLIENT password reset by email OR by the portal name they
   * log in with (PUBLIC, no auth). NON-ENUMERATING: an identical generic success
   * is returned whether or not a client matched; a `429` means the per-IP
   * request ceiling was crossed.
   */
  forgot: (body: ClientForgotPayload) =>
    http.post<ForgotResponse>("/client/forgot", body),
  /**
   * Wave 8 — consume a CLIENT reset token and set a new password (PUBLIC). On
   * success the server signs the client in (vf_client_session cookie) and
   * returns the freshly-authenticated client; a `400` means an invalid /
   * expired / already-used token, a `422` a too-short password (< 8 chars).
   */
  reset: (body: PasswordResetPayload) =>
    http.post<ClientAuthResponse>("/client/reset", body),
  /**
   * Wave 8 — change the signed-in CLIENT's own password (authed self-service).
   * Verifies `current_password` (a `401` mirrors a failed login) before setting
   * the new one (>= 8 chars; `422` otherwise). Scoped to the caller's own row.
   */
  changePassword: (body: ClientPasswordChangePayload) =>
    http.post<OkResponse>("/client/me/password", body),

  // --- aggregated views ---------------------------------------------------
  dashboard: (signal?: AbortSignal) =>
    http.get<ClientDashboard>("/client/me/dashboard", undefined, signal),
  /**
   * ONE call powering the fancy client dashboard (Wave 4): profile, today's
   * nutrition intake+targets+water (for the rings), this week's coach-goal
   * progress, next session, pending homework count, the merged recent-activity
   * feed, and the weight trend — all the caller's OWN data, no second round trip.
   */
  home: (signal?: AbortSignal) =>
    http.get<ClientHome>("/client/me/home", undefined, signal),
  metrics: (signal?: AbortSignal) =>
    http.get<MetricsSummary>("/client/me/metrics", undefined, signal),
  report: (period = "all", signal?: AbortSignal) =>
    http.get<ClientReport>("/client/me/report", { period }, signal),

  // --- self-service reads/writes -----------------------------------------
  measurements: (signal?: AbortSignal) =>
    http.get<Measurement[]>("/client/me/measurements", undefined, signal),
  addMeasurement: (body: MeasurementInput) =>
    http.post<Measurement>("/client/me/measurements", body),
  sessions: (signal?: AbortSignal) =>
    http.get<Session[]>("/client/me/sessions", undefined, signal),
  addSession: (body: SessionInput) =>
    http.post<Session>("/client/me/sessions", body),
  goals: (activeOnly = true, signal?: AbortSignal) =>
    http.get<Goal[]>("/client/me/goals", { active_only: activeOnly }, signal),

  // --- activities (own self-logged + trainer-suggested homework) ----------
  /** This client's own activities (self-logged + suggested homework). */
  activities: (signal?: AbortSignal) =>
    http.get<Activity[]>("/client/me/activities", undefined, signal),
  /** Self-log an exercise the CLIENT did (provenance forced to 'own'). */
  logActivity: (body: ClientActivityInput) =>
    http.post<Activity>("/client/me/activities", body),
  /** Mark a trainer-SUGGESTED activity (homework) done. */
  completeActivity: (activityId: number) =>
    http.post<Activity>(`/client/me/activities/${activityId}/complete`),
  /** The merged calendar union (own PT sessions + own/suggested activities). */
  calendarEntries: (range: ClientCalendarRange = {}, signal?: AbortSignal) =>
    http.get<ClientCalendarEntry[]>(
      "/client/me/calendar-entries",
      range,
      signal,
    ),

  // --- nutrition: meals (full macros, optional photo) --------------------
  /** This client's meals + the day's macro totals; `day=YYYY-MM-DD` to filter. */
  meals: (day?: string, signal?: AbortSignal) =>
    http.get<MealListResponse>("/client/me/meals", { day }, signal),
  /** Self-log a meal (provenance forced to this client's owning trainer). */
  addMeal: (body: MealInput) => http.post<Meal>("/client/me/meals", body),
  /** Delete one of THIS client's meals (own only). */
  deleteMeal: (mealId: number) =>
    http.del<OkResponse>(`/client/me/meals/${mealId}`),
  /** Attach an encrypted photo (≤2 MB) to one of THIS client's meals. */
  uploadMealPhoto: (mealId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return http.upload<MealPhotoResponse>(
      `/client/me/meals/${mealId}/photo`,
      form,
    );
  },
  /** Absolute URL of a meal's decrypt-on-read photo (own only) for an <img>. */
  mealPhotoUrl: (mealId: number) =>
    `${API_BASE}/client/me/meals/${mealId}/photo`,

  // --- nutrition: water (per-day incremental) ----------------------------
  /** This client's daily water rows (oldest day first). */
  water: (signal?: AbortSignal) =>
    http.get<WaterLog[]>("/client/me/water", undefined, signal),
  /** Log water for a day (increment by `delta_ml`, or set absolute `ml`). */
  addWater: (body: WaterInput) => http.post<WaterLog>("/client/me/water", body),

  // --- nutrition: auto daily targets (general guidance) ------------------
  /** Auto daily kcal/macro/water targets for THIS client (with disclaimer). */
  nutritionTargets: (signal?: AbortSignal) =>
    http.get<NutritionTargets>(
      "/client/me/nutrition-targets",
      undefined,
      signal,
    ),

  // --- weekly goal: THIS week's coach goal + progress checklist (Wave 3) --
  /**
   * THIS client's current-week weekly goal + progress (read-only). Returns the
   * coach's targets/exercise homework for the week (or null) plus the client's
   * logged-vs-target progress. The client can never WRITE a weekly goal.
   */
  weeklyGoal: (signal?: AbortSignal) =>
    http.get<ClientWeeklyGoalResponse>(
      "/client/me/weekly-goal",
      undefined,
      signal,
    ),

  // --- messaging: THIS client's single thread with their trainer (Wave 5) -
  // A thread is exactly one (trainer, client) pair; there is no id in the URL
  // (the server resolves "me"). `body` is plain UTF-8 user text + unicode emoji,
  // rendered ESCAPED by the SPA. Images go through the multipart attachment
  // endpoint and are fetched back via the owner-gated decrypt-serve URL.
  messages: {
    /** The thread (oldest→newest for display) + unread count. */
    list: (
      params: { limit?: number; before_id?: number } = {},
      signal?: AbortSignal,
    ) =>
      http.get<MessageListResponse>(
        "/client/me/messages",
        { limit: params.limit, before_id: params.before_id },
        signal,
      ),
    /** Unread (trainer→client) message count for the badge. */
    unread: (signal?: AbortSignal) =>
      http.get<MessageUnreadResponse>(
        "/client/me/messages/unread",
        undefined,
        signal,
      ),
    /** Post a text message (sender forced to 'client' server-side). */
    send: (body: MessageInput) =>
      http.post<Message>("/client/me/messages", body),
    /** Mark the thread read (stamps read_at on the TRAINER's unread messages). */
    markRead: () => http.post<{ ok: boolean; marked: number }>(
      "/client/me/messages/read",
    ),
    /** Send an image attachment (≤2 MB enforced server-side) + optional caption. */
    sendAttachment: (file: File, body?: string) => {
      const form = new FormData();
      form.append("file", file);
      return http.upload<MessageAttachmentResponse>(
        "/client/me/messages/attachment",
        form,
        body ? { body } : undefined,
      );
    },
    /** Absolute URL of a thread image's decrypt-on-read serve endpoint. */
    attachmentUrl: (photoId: number) =>
      `${API_BASE}/client/me/messages/attachment/${photoId}`,
  },

  // --- calendar (ICS) subscription (own feed only) -----------------------
  // The client's private, read-only feed of THEIR training only. Same shape and
  // posture as the trainer feed; the token is the secret, rotate revokes a leak.
  calendarSubscription: (signal?: AbortSignal) =>
    http.get<CalendarSubscription>(
      "/client/me/calendar/subscription",
      undefined,
      signal,
    ),
  rotateCalendarSubscription: () =>
    http.post<CalendarSubscription>("/client/me/calendar/subscription/rotate"),
};
